<template>
  <div class="comments-list" :class="backgroundColor">
    <CommentCard
      v-for="comment in comments"
      :key="comment._id"
      :accounts="comment.accounts"
      :comment="comment.content"
      :createdBy="comment.createdBy"
      :commentDate="getCommentDate(comment)"
      :ownerName="getUserName(comment)"
      :canEdit="canEdit"
      :isCommentOwner="isCommentOwner(comment.createdBy)"
      :isHighlighted="
        Boolean(highlightedCommentId) && highlightedCommentId === comment._id
      "
      :organizationId="organizationId"
      :userId="getUserId(comment)"
      :commentLanguage="comment.lang"
      :commentId="comment.id"
      @onDeleteComment="onDeleteComment(comment._id)"
      @reply="$emit('reply', comment.createdBy)"
    />
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :isLoading="isDeleting"
      :title="$t('comments.delete_comment')"
      :text="deleteConfirmationText"
      @cancel="showDeleteModal = false"
      @delete="deleteComment"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'

import CommentCard from '@/components/Core/Comments/CommentCard.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

export default {
  name: 'CommentsList',

  mixins: [mixin],

  components: {
    CommentCard,
    DeleteConfirmationModal,
  },

  props: {
    comments: {
      type: Array,
      required: true,
    },
    canEdit: {
      type: Boolean,
      required: false,
    },
    modelName: {
      type: String,
      required: true,
    },
    modelType: {
      type: String,
      required: false,
    },
    modelId: {
      type: String,
      required: true,
    },

    userOrganizationId: {
      type: String,
      required: false,
    },

    handleExternalDelete: {
      type: Boolean,
      required: false,
    },

    backgroundColor: {
      type: String,
      required: false,
      default: 'white',
    },

    highlightedCommentId: String,
  },

  data() {
    return {
      showDeleteModal: false,
      isDeleting: false,
      commentToDelete: '',
      organizationId:
        this.getRouteParams().organizationId || this.userOrganizationId,
    }
  },

  methods: {
    ...mapActions({
      removeComment: 'comments/removeComment',
      getCommentsByModelId: 'comments/getCommentsByModelId',
    }),

    getCommentDate(comment) {
      return utils.dates.humanDate(comment.createdAt)
    },

    getUserName(comment) {
      return `${comment?.createdBy?.displayname ?? ''} ${comment?.createdBy
        ?.familyname ?? ''}`
    },

    getUserId(comment) {
      return comment?.createdBy?.id
    },

    onDeleteComment(commentId) {
      this.showDeleteModal = true
      this.commentToDelete = commentId
    },

    isCommentOwner(commentOwner) {
      const accountId = this.getAccountId()
      return commentOwner._id === accountId
    },

    hideModal() {
      this.showDeleteModal = false
    },

    async deleteComment() {
      try {
        this.isDeleting = true
        await this.removeComment({
          organizationId: this.organizationId,
          commentId: this.commentToDelete,
        })

        if (this.handleExternalDelete) {
          this.$emit('commentHasBeenDeleted')
        } else {
          this.getCommentsByModelId({
            organizationId: this.organizationId,
            modelName: this.modelName,
            modelId: this.modelId,
          })
        }
      } catch (e) {
        this.showError(
          this.$t(
            'error.500.something_bad_happened_we_probably_did_something_wrong'
          )
        )
      } finally {
        this.isDeleting = false
        this.hideModal()
      }
    },
  },

  computed: {
    deleteConfirmationText() {
      if (this.modelType === 'Task') {
        return this.$t(
          'comments.are_you_sure_you_want_to_remove_the_comment_from_this_task',
          {
            model: this.modelType,
          }
        )
      }
      return this.$t(
        'comments.are_you_sure_you_want_to_remove_this_comment_from_this',
        {
          model: this.modelType,
        }
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.comments-list {
  min-height: 100%;
}
</style>
